import { ArrowUpRight, Layers, Sparkles } from "lucide-react";
import type { ComponentType } from "react";

import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import type { Confidence, Tier } from "./types";

interface TierMeta {
  label: string;
  explanation: string;
  chip: string;
  icon: ComponentType<{ className?: string }>;
}

// The single source of truth for how a tier reads anywhere in the UI —
// consistency is what teaches the three-tier mental model.
const TIER_META: Record<Tier, TierMeta> = {
  1: {
    label: "T1 Self-serve",
    explanation:
      "Tier 1 — fully automated: text layers, page selection, sequencing, length and language inside the template rules.",
    chip: "border-outcome-applied/40 bg-outcome-applied-tint text-outcome-applied",
    icon: Sparkles,
  },
  2: {
    label: "T2 Overlay",
    explanation:
      "Tier 2 — translated overlay on a fixed layout. Automated, but flagged because the copy may not fit the original design.",
    chip: "border-outcome-escalated/40 bg-outcome-escalated-tint text-outcome-escalated",
    icon: Layers,
  },
  3: {
    label: "T3 Escalated",
    explanation:
      "Tier 3 — needs the source design file. The tool does not attempt it; it drafts a structured request for the Strategic Comms team instead.",
    chip: "border-outcome-routed/40 bg-outcome-routed-tint text-outcome-routed",
    icon: ArrowUpRight,
  },
};

const CONFIDENCE_NOTE: Record<Confidence, string> = {
  high: "fit confirmed",
  medium: "fit uncertain — check",
  low: "fit unlikely — check",
};

interface TierBadgeProps {
  tier: Tier;
  /** Tier-2 only: how sure the engine is that the overlay fits the layout. */
  confidence?: Confidence | null;
  /** Hide the wording, keep the icon — for dense rows such as the tier counts. */
  compact?: boolean;
  className?: string;
}

export function TierBadge({ tier, confidence, compact = false, className }: TierBadgeProps) {
  // Fall back to tier 1 rather than crashing if the backend ever widens `tier`.
  const meta = TIER_META[tier] ?? TIER_META[1];
  const Icon = meta.icon;
  const note = tier === 2 && confidence ? (CONFIDENCE_NOTE[confidence] ?? null) : null;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            "inline-flex w-fit shrink-0 cursor-help items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] leading-4 font-semibold",
            meta.chip,
            className,
          )}
        >
          <Icon className="h-3 w-3" />
          {compact ? `T${tier}` : meta.label}
          {note && !compact && (
            <span className="ml-0.5 border-l border-current/30 pl-1.5 font-normal italic">
              {note}
            </span>
          )}
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs text-xs leading-5">
        {meta.explanation}
        {note && ` (${note})`}
      </TooltipContent>
    </Tooltip>
  );
}

export { TIER_META };
